import { Component, OnInit, OnDestroy } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { ProfessionalStateService } from '../../services/professional-state.service';
import { NotificationService } from '../../services/notification.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-cost-centers',
  templateUrl: './cost-centers.component.html',
  styleUrls: ['./cost-centers.component.scss']
})
export class CostCentersComponent implements OnInit, OnDestroy {
  result: any = { items: [], totalCount: 0, page: 1, pageSize: 20 };
  loading = false;
  professionalId = 0;
  filter: any = { page: 1, pageSize: 20, search: '', isActive: null };

  showModal = false;
  saving = false;
  editingId: number | null = null;
  form: any = {
    name: '', description: '', generatesRevenue: false, parentId: null
  };

  // Seleção de centro de custo pai
  allCostCenters: any[] = [];
  filteredCostCenters: any[] = [];
  parentSearchTerm = '';
  showParentDropdown = false;
  selectedParent: any = null;

  private searchInput$ = new Subject<string>();
  private subs = new Subscription();

  constructor(
    private api: ApiService,
    private profState: ProfessionalStateService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.searchInput$.pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(term => { this.filter.search = term; this.filter.page = 1; this.load(); })
    );

    this.subs.add(
      this.profState.selected$.subscribe(p => {
        if (p) { this.professionalId = p.id; this.load(); this.loadAllCostCenters(); }
      })
    );
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  loadAllCostCenters(): void {
    // Carrega todos os centros de custo ativos para seleção de centro pai
    this.api.getCostCenters(this.professionalId, { pageSize: 500, isActive: true }).subscribe({
      next: r => {
        this.allCostCenters = r.items;
        this.filteredCostCenters = r.items;
      },
      error: () => {}
    });
  }

  load(): void {
    this.loading = true;
    this.api.getCostCenters(this.professionalId, this.filter).subscribe({
      next: r => { this.result = r; this.loading = false; },
      error: () => this.loading = false
    });
  }

  onSearch(term: string): void { this.searchInput$.next(term); }
  onPageChange(page: number): void { this.filter.page = page; this.load(); }
  onSizeChange(size: number): void { this.filter.pageSize = size; this.filter.page = 1; this.load(); }
  onStatusChange(status: string): void {
    this.filter.isActive = status === '' ? null : status === 'true';
    this.filter.page = 1;
    this.load();
  }

  openModal(costCenter?: any): void {
    this.editingId = costCenter?.id ?? null;
    this.form = costCenter ? { ...costCenter } : {
      name: '', description: '', generatesRevenue: false, parentId: null
    };

    // Configurar centro de custo pai selecionado
    if (costCenter?.parentId) {
      const parent = this.allCostCenters.find(c => c.id === costCenter.parentId);
      this.selectedParent = parent || null;
    } else {
      this.selectedParent = null;
    }
    this.parentSearchTerm = '';
    this.showParentDropdown = false;

    this.showModal = true;
  }

  filterParentCostCenters(): void {
    const term = this.parentSearchTerm.toLowerCase();
    // Filtrar centros de custo excluindo o atual (não pode ser pai de si mesmo)
    this.filteredCostCenters = this.allCostCenters.filter(c =>
      c.id !== this.editingId &&
      c.name.toLowerCase().includes(term)
    );
  }

  onParentSearchChange(term: string): void {
    this.parentSearchTerm = term;
    this.filterParentCostCenters();
    this.showParentDropdown = term.length > 0 || this.filteredCostCenters.length > 0;
  }

  selectParent(costCenter: any): void {
    this.selectedParent = costCenter;
    this.form.parentId = costCenter.id;
    this.parentSearchTerm = '';
    this.showParentDropdown = false;
  }

  clearParent(): void {
    this.selectedParent = null;
    this.form.parentId = null;
    this.parentSearchTerm = '';
    this.showParentDropdown = false;
  }

  focusParentSearch(): void {
    this.filterParentCostCenters();
    this.showParentDropdown = true;
  }

  save(): void {
    if (!this.form.name?.trim()) {
      this.notification.warning('Atenção', 'Nome é obrigatório');
      return;
    }

    this.saving = true;
    const op = this.editingId
      ? this.api.updateCostCenter(this.professionalId, this.editingId, this.form)
      : this.api.createCostCenter(this.professionalId, this.form);

    op.subscribe({
      next: () => {
        this.saving = false;
        this.showModal = false;
        this.notification.success('Sucesso!', `Centro de custo ${this.editingId ? 'atualizado' : 'cadastrado'} com sucesso`);
        this.load();
      },
      error: () => {
        this.saving = false;
        this.notification.error('Erro', 'Erro ao salvar centro de custo');
      }
    });
  }

  delete(costCenter: any): void {
    const action = costCenter.isActive ? 'Inativar' : 'Ativar';
    this.notification.confirm(
      'Confirmar Ação',
      `Tem certeza que deseja ${action.toLowerCase()} o centro de custo ${costCenter.name}?`,
      () => {
        this.api.deleteCostCenter(this.professionalId, costCenter.id).subscribe({
          next: () => {
            this.notification.success('Sucesso!', `Centro de custo ${action.toLowerCase()}do com sucesso`);
            this.load();
          },
          error: () => this.notification.error('Erro', `Erro ao ${action.toLowerCase()} centro de custo`)
        });
      }
    );
  }

  fmtDate(d?: string): string {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  }
}
